import * as React from 'react';
import {Select} from '@packages/systems/spring';
import Hitarea from '@packages/systems/design-system/Hitarea';
import type {State} from './LinkTypes';

type PreloadValue = State['preload'];

// Preload
export const usePreload = (state: State, dispatch: (arg1?: any) => void) => {
  const [preload, setPreload] = React.useState<PreloadValue>(state.preload);

  const onChange = React.useCallback(
    (newPreload: string | null | undefined) => {
      const value = (newPreload ?? undefined) as PreloadValue;
      setPreload(value);
      dispatch({
        payload: {...state, preload: value},
        meta: {
          analytics: {
            type: 'element-settings-updated',
            value: 'link preload',
          },
        },
      });
    },
    [dispatch, state]
  );

  React.useEffect(() => setPreload(state.preload), [state.preload]);

  // When `preload` is undefined we set the state to show the default UI.
  React.useEffect(() => {
    if (state.preload === undefined) {
      setPreload('none');
    }
  }, [state]);

  return {onChange, preload};
};

const preloadOptions = [
  {
    value: 'prefetch',
    label: 'Prefetch',
    note:
      'Browser downloads linked page resources after the initial page load.' +
      ' Only use for important links visitors are likely to click.',
  },
  {
    value: 'prerender',
    label: 'Prerender',
    note:
      'Browser downloads linked page resources immediately.' +
      ' Only use for primary links visitors are very likely to click.',
  },
  {
    value: 'none',
    label: 'Default',
    note: 'Browser downloads linked page when visitor clicks.',
  },
];

export const LinkPreload = ({
  state,
  dispatch,
}: {
  state: State;
  dispatch: (arg1?: any) => void;
}) => {
  const {preload, onChange} = usePreload(state, dispatch);

  return (
    <Hitarea>
      <Select
        aria-label="Preload"
        data-automation-id="Type--Plugin_Enum_Preload_menu"
        id="Type--Plugin_Enum_Preload_menu"
        onChange={onChange}
        options={preloadOptions}
        shouldPositionInline
        value={preload}
      />
    </Hitarea>
  );
};
